import Component from '@glimmer/component';
import EmberObject, { action, get } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { scheduleTask } from 'ember-lifeline';
import IdeaValidations from 'scaleflow/validators/idea';
import { Changeset } from 'ember-changeset';
import ImageUploader from 'quill2-image-uploader';
import ImageFormat from 'scaleflow/utils/quill-extenstions/image-format';
import Quill from 'quill';
import imageResizeOptions from 'scaleflow/utils/quill-extenstions/image-resize';
import registerIfNotRegistered from 'scaleflow/utils/quill-extenstions/registerIfNotRegistered';
import imageUploadHandler from 'scaleflow/utils/quill-extenstions/image-uploader';

// Register Quill modules
Quill.register('formats/image', ImageFormat, true);
registerIfNotRegistered(Quill, 'modules/imageUploader', ImageUploader);

const description_template = {
  ops: [
    {
      attributes: {
        bold: true,
      },
      insert: 'How will this idea help?',
    },
    {
      insert: '\n\n\n',
    },
    {
      attributes: {
        bold: true,
      },
      insert: 'How should it work?',
    },
    {
      insert: '\n\n\n',
    },
    {
      attributes: {
        bold: true,
      },
      insert: 'Who will benefit from this idea?',
    },
    {
      insert: '\n\n\n\n\n',
    },
  ],
};
export default class IdeasWizardComponent extends Component {
  @service router;
  @service store;
  @service betaToggle;
  @service intl;
  @service currentUser;
  @service organisation;
  @service session;

  ideaValidations = IdeaValidations;

  @tracked activeStep = 0;
  ideaChangeset = EmberObject.create({});
  @tracked selectedDepartments = [];
  @tracked selectedAreas = [];
  @tracked errorMessage = '';
  @tracked showDeleteModal = false;
  @tracked selectedIdea = null;
  @tracked selectedThemes = [];
  @tracked selectedCustomers = [];
  @tracked formIsInitialized = false;

  datePickerInstance = null;

  get idea() {
    return this.args.idea;
  }

  @action
  setActiveStep(index) {
    this.activeStep = index;
  }

  @action
  handleThemesChange(themeIds) {
    const themes = themeIds.map((id) => this.store.peekRecord('theme', id));
    this.selectedThemes = themes;
  }

  @action
  handleDepartmentsChange(departmentIds) {
    const departments = departmentIds.map((id) =>
      this.store.peekRecord('department', id),
    );
    this.selectedDepartments = departments;
  }

  @action
  handleAreasChange(areaIds) {
    const areas = areaIds.map((id) => this.store.peekRecord('area', id));
    this.selectedAreas = areas;
  }

  @action
  handleCustomersChange(customerIds) {
    const customers = customerIds.map((id) =>
      this.store.peekRecord('customer', id),
    );
    this.selectedCustomers = customers;
  }

  get themesList() {
    const themes = this.store.peekAll('theme').map((item) => ({
      value: item.id,
      label: item.title,
      color: item.color,
    }));
    return themes;
  }

  get selectedThemeIds() {
    return this.selectedThemes.map((goal) => goal.id);
  }

  get firstTheme() {
    return this.selectedThemes?.[0];
  }

  get hasMoreThanOneTheme() {
    return this.selectedThemes.length > 1
      ? this.selectedThemes.length - 1
      : false;
  }

  get selectedDepartmentIds() {
    return this.selectedDepartments.map((department) => department.id);
  }

  get firstDepartment() {
    return this.selectedDepartments?.[0];
  }

  get hasMoreThanOneDepartment() {
    return this.selectedDepartments.length > 1
      ? this.selectedDepartments.length - 1
      : false;
  }

  get selectedAreaIds() {
    return this.selectedAreas.map((area) => area.id);
  }

  get firstArea() {
    return this.selectedAreas?.[0];
  }

  get hasMoreThanOneArea() {
    return this.selectedAreas.length > 1
      ? this.selectedAreas.length - 1
      : false;
  }

  get statusOptions() {
    return this.betaToggle.ideaStatusOptions;
  }

  get modules() {
    const modules = {};
    modules.imageUploader = {
      upload: (file) => {
        return imageUploadHandler(
          this.session.idToken,
          this.organisation.id,
          file,
        ).then((s3Link) => {
          return s3Link;
        });
      },
    };
    modules.resize = imageResizeOptions(this.intl);

    let toolbarOptions = [
      ['bold', 'italic', 'strike'], // toggled buttons
      ['blockquote', 'code-block'],
      [{ header: 1 }, { header: 2 }], // custom button values
      [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
      ['link', 'image', 'clean'], // remove formatting button
    ];
    modules.toolbar = toolbarOptions;
    return modules;
  }

  @action
  handleStatusSelect(changeset, status) {
    changeset.set('status', status.value);
  }

  @action
  clearStatusSelect(changeset) {
    changeset.set('status', null);
  }

  @action
  onDescriptionChange(changeset, delta) {
    changeset.set('description', delta);
  }

  @action
  handleDateChange(changeset, date) {
    changeset.set('deadline', date);
  }

  @action
  onDatePickerReady(instance) {
    this.datePickerInstance = instance;
  }

  @action
  openDatePicker() {
    this.datePickerInstance?.open();
  }

  @action
  goToListPage() {
    this.router.transitionTo('backlog.ideas.list', {
      queryParams: {
        refresher: this.router.currentRoute?.queryParams.refresher
          ? !this.router.currentRoute.queryParams.refresher
          : true,
      },
    });
  }

  @action
  closeWizard() {
    this.goToListPage();
  }

  @action
  cancelForm() {
    this.ideaChangeset?.rollback?.();
    if (!this.idea.id) {
      this.idea.deleteRecord();
    }
    this.closeWizard();
  }

  @action
  deleteIdea() {
    if (this.selectedIdea) {
      this.selectedIdea
        .destroyRecord()
        .then(() => {
          this.goToListPage();
          this.handleDismissDeleteModal();
        })
        .catch((err) => {
          const error = err.errors?.[0];
          if (!error?.detail) return;
          const message = JSON.parse(error.detail)?.[0]?.[1];
          if (!message) return;
          this.errorMessage = message;
        });
    }
  }
  @action
  async saveAndClose() {
    try {
      const idea = this.idea;
      idea.mapChangesetToAttributes(this.ideaChangeset.changes);
      // Set departments relationship
      if (this.selectedDepartments.length) {
        idea.set('departments', this.selectedDepartments);
      }
      // Set areas relationship
      if (this.selectedAreas.length) {
        idea.set('areas', this.selectedAreas);
      }

      // Set themes relationship
      if (this.selectedThemes.length) {
        idea.set('themes', this.selectedThemes);
      }

      // Set customers relationship
      if (this.selectedCustomers.length) {
        idea.set('customers', this.selectedCustomers);
      }

      idea.set('submitter', this.currentUser.currentUser);

      await idea.save();
      this.closeWizard();
    } catch (error) {
      console.error('Error saving idea:', error);
      this.errorMessage = 'Failed to save idea. Please try again.';
    }
  }

  @action
  isStep1Invalid(changeset) {
    // Check if title is empty or untouched AND description is empty/untouched
    const titleEmpty =
      !changeset.get('title') || changeset.get('title').trim() === '';
    const descriptionEmpty =
      !changeset.get('description') ||
      !changeset.get('description').ops ||
      changeset.get('description').ops.length === 0 ||
      (changeset.get('description').ops.length === 1 &&
        changeset.get('description').ops[0].insert === '\n');

    return titleEmpty || descriptionEmpty;
  }

  @action
  handleAlertClose() {
    this.errorMessage = '';
  }

  @action
  ideaValidator({ key, newValue, oldValue, changes, content }) {
    let validator = get(this.ideaValidations, key);
    if (typeof validator === 'function') {
      return validator(newValue, oldValue, changes, content);
    }
  }

  get departmentsList() {
    const departments = this.store
      .peekAll('department')
      .filter((d) => !d.isNew)
      .map((item) => ({
        value: item.id,
        label: item.name,
      }));
    return departments;
  }

  get areasList() {
    const areas = this.store
      .peekAll('area')
      .filter((a) => !a.isNew)
      .map((item) => ({
        value: item.id,
        label: item.name,
      }));
    return areas;
  }

  get customersList() {
    const customers = this.store
      .peekAll('customer')
      .filter((c) => !c.isNew)
      .map((item) => ({
        value: item.id,
        label: item.name,
      }));
    return customers;
  }

  get selectedCustomerIds() {
    return this.selectedCustomers.map((customer) => customer.id);
  }

  get firstCustomer() {
    return this.selectedCustomers?.[0];
  }

  get hasMoreThanOneCustomer() {
    return this.selectedCustomers.length > 1
      ? this.selectedCustomers.length - 1
      : false;
  }

  @action
  clearDeadline(changeset) {
    changeset.set('deadline', null);
  }

  get isEdit() {
    return !!this.idea?.id;
  }

  prepopulateFields(changeset) {
    changeset.set('status', 'NEW');
    changeset.set('description', description_template);
  }

  @action
  initializeForm() {
    scheduleTask(this, 'actions', () => {
      const ideaChangeset = Changeset(
        this.idea.changeset_object,
        this.ideaValidator,
      );
      this.ideaChangeset = ideaChangeset;
      if (!this.idea.id) this.prepopulateFields(ideaChangeset);
      this.selectedDepartments = this.idea.departments?.content || [];
      this.selectedAreas = this.idea.areas?.content || [];
      this.selectedThemes = this.idea.themes?.content || [];
      this.selectedCustomers = this.idea.customers?.content || [];
      this.formIsInitialized = true;
    });
  }

  @action
  toggleDeleteModal(flag) {
    if (flag === undefined) this.showDeleteModal = !this.showDeleteModal;
    else this.showDeleteModal = flag;
  }

  @action
  handleDismissDeleteModal() {
    this.selectedIdea = null;
    this.toggleDeleteModal(false);
  }

  @action
  setInvestmentValue(changeset, event) {
    const ppw = this.organisation.ppw;
    const effort = +event.target.value;
    if (isNaN(ppw) && isNaN(effort)) return;
    changeset.set('investment', ppw * effort);
  }

  @action
  startDeleteProcess() {
    this.toggleDeleteModal(true);
    this.selectedIdea = this.idea;
  }

  /**
   * Quick add functions
   */

  @tracked departmentFormIsVisible = false;
  @tracked customerFormIsVisible = false;
  @tracked areaFormIsVisible = false;
  @tracked themeFormisVisible = false;

  @action
  toggleDepartmentForm(val) {
    if (val !== undefined) this.departmentFormIsVisible = val;
    else this.departmentFormIsVisible = !this.departmentFormIsVisible;
  }

  @action
  handleAddDepartmentToIdea(department) {
    const departments = [...this.selectedDepartments];
    departments.push(department);
    this.selectedDepartments = departments;
  }

  @action
  toggleCustomerForm(val) {
    if (val !== undefined) this.customerFormIsVisible = val;
    else this.customerFormIsVisible = !this.customerFormIsVisible;
  }

  @action
  handleAddCustomerToIdea(customer) {
    const customers = [...this.selectedCustomers];
    customers.push(customer);
    this.selectedCustomers = customers;
  }

  @action
  toggleAreaForm(val) {
    if (val !== undefined) this.areaFormIsVisible = val;
    else this.areaFormIsVisible = !this.areaFormIsVisible;
  }

  @action
  handleAddAreaToIdea(area) {
    const areas = [...this.selectedAreas];
    areas.push(area);
    this.selectedAreas = areas;
  }

  @action
  toggleThemeForm(val) {
    if (val !== undefined) this.themeFormisVisible = val;
    else this.themeFormisVisible = !this.themeFormisVisible;
  }

  @action
  handleAddThemeToIdea(theme) {
    const themes = [...this.selectedThemes];
    themes.push(theme);
    this.selectedThemes = themes;
  }
}
